/* eslint-disable @typescript-eslint/no-shadow */
import { FieldValues, SubmitHandler, useForm } from 'react-hook-form'
import { ForwardedRef, useEffect, useMemo, useRef } from 'react'
import { zodFormResolver } from '~/utils/zodFormResolver'
import { useRouter } from 'next/router'
import { addDays, differenceInCalendarDays, differenceInDays, format, isSameDay } from 'date-fns'
import { stringify } from 'qs'
import { DEFAULT_RECOMMENDED_SORTING } from '@reward-platform/ancillaries-schemas/common'
import {
  CarHireLocation,
  findNearestOpenDate,
  getCommonBrandIds,
  getQualifiedBrandIds,
  getServingOpeningTimes,
  getVendorDesksForRequest,
} from '@reward-platform/ancillaries-locations'
import { DatePickerProps } from '@reward-platform/lift/components/DatePicker/DatePicker.types'
import { usePlatformConfig, usePlatformRoutes } from '~/context/platform'
import { useFeatureFlag } from '~/context/featureFlag'
import useFilterRouter from '~/context/filters/useFilterRouter'
import { parseCarHireLocationFromUrl } from '~/utils/locationComparison'
import { useLocationChanged } from '~/hooks/useLocationChanged'
import { CarHireSearchForm } from '../SearchCarHireForm.types'
import { useCarHireSearchFormValidation } from './useCarHireSearchFormValidation'
import { useCarHireSearchFormDates } from './useCarHireSearchFormDates'
import {
  setHoursandMinutesOnDate,
  getHoursMinutesFrom24hrString,
  addHourTo24hrString,
} from '../helpers'

const SEARCH_TYPE = 'CAR_HIRE'

const DATETIME_FORMAT = "y-MM-dd'T'HH:mm"

export const MAX_DAYS_RENTAL = 27
const DEFAULT_RENTAL_LENGTH_DAYS = 2

const getLocationSearchData = (
  location: CarHireLocation,
  dates: Date[],
  multiBrandEnabled: boolean
) => {
  if (!multiBrandEnabled) {
    return { id: location.id, type: location.type }
  }

  const vendorDesks = getVendorDesksForRequest(location, dates)
  return {
    id: location.id,
    type: location.type,
    ...(location.countryCode ? { countryCode: location.countryCode } : {}),
    ...(vendorDesks?.length ? { vendorDesks } : {}),
  }
}

const getBrandSearchData = (
  multiBrandEnabled: boolean,
  qualifiedBrandIds: string[],
  pickUpLocation: CarHireLocation
) => ({
  vendorBrandId: multiBrandEnabled
    ? qualifiedBrandIds[0] ?? pickUpLocation.vendorBrandId
    : pickUpLocation.vendorBrandId,
  ...(multiBrandEnabled && { vendorBrandIds: qualifiedBrandIds }),
})

const getQualifiedBrandIdsForRequest = (
  multiBrandEnabled: boolean,
  pickUpLocation: CarHireLocation,
  dropOffLocation: CarHireLocation | undefined,
  pickUpDateTime: Date,
  dropOffDateTime: Date
) =>
  multiBrandEnabled
    ? getQualifiedBrandIds({
        pickUpLocation,
        dropOffLocation,
        pickUpDateTime,
        dropOffDateTime,
      })
    : []

const getOpeningTimesForLocation = (
  multiBrandEnabled: boolean,
  location: CarHireLocation | undefined,
  servingBrandIds: string[],
  allowAfterHoursDropOff = false
) =>
  getServingOpeningTimes(location, multiBrandEnabled ? servingBrandIds : [], allowAfterHoursDropOff)

// eslint-disable-next-line sonarjs/cognitive-complexity
export function useCarHireSearchForm(
  defaultValues: CarHireSearchForm,
  toggleRef?: ForwardedRef<{ press: () => void }>
) {
  const router = useRouter()
  const { query: filtersQuery } = useFilterRouter('CAR_HIRE')
  const { createRoutePath } = usePlatformRoutes()
  const { opCo } = usePlatformConfig()
  const appliedPickUpLocationRef = useRef('')
  const appliedDropOffLocationRef = useRef('')
  const multiBrandEnabled = useFeatureFlag('car-hire-multi-brand')
  const { minPickUpDate, maxPickUpDate } = useCarHireSearchFormDates()
  const CarHireSearchFormValidationSchema = useCarHireSearchFormValidation()
  const form = useForm<CarHireSearchForm>({
    defaultValues,
    resolver: zodFormResolver(CarHireSearchFormValidationSchema),
  })

  // Reset filters if location changed; preserve if only dates/times changed
  const hasLocationChanged = useLocationChanged(parseCarHireLocationFromUrl)

  const {
    formState: { errors },
    setValue,
    watch,
  } = form

  // Watch every value that can affect automatic date selection or associated form rendering.
  const [
    isUnderAgeLimit,
    differentDropOff,
    isAvisPreferred,
    pickUpLocation,
    dropOffLocation,
    pickUpDate,
    pickUpTime,
    dropOffDate,
  ] = watch([
    'isUnderAgeLimit',
    'differentDropOff',
    'isAvisPreferred',
    'pickUpLocation',
    'dropOffLocation',
    'pickUpDate',
    'pickUpTime',
    'dropOffDate',
  ])

  const servingBrandIds = multiBrandEnabled
    ? getCommonBrandIds(pickUpLocation, differentDropOff ? dropOffLocation : undefined)
    : []
  const pickUpOpeningTimes = getOpeningTimesForLocation(
    multiBrandEnabled,
    pickUpLocation,
    servingBrandIds
  )
  const returnLocation = differentDropOff ? dropOffLocation : pickUpLocation
  const dropOffOpeningTimes = getOpeningTimesForLocation(
    multiBrandEnabled,
    returnLocation,
    servingBrandIds,
    true
  )

  const maxDropOffDate = useMemo(() => addDays(pickUpDate, MAX_DAYS_RENTAL), [pickUpDate])

  const setDateState =
    (setStateFunc: (date: Date) => void): DatePickerProps['onChange'] =>
    (date) => {
      if (!date || Array.isArray(date)) {
        return
      }
      form.clearErrors()
      setStateFunc(date)
    }

  const updatePickUpDate = (date: Date) => {
    const availablePickUpDate =
      findNearestOpenDate(pickUpOpeningTimes, date, minPickUpDate, maxPickUpDate) ?? date
    const minimumAutomaticDropOffDate = addDays(availablePickUpDate, DEFAULT_RENTAL_LENGTH_DAYS)
    const maximumDropOffDate = addDays(availablePickUpDate, MAX_DAYS_RENTAL)
    const shouldResetDropOffDate =
      dropOffDate.valueOf() < minimumAutomaticDropOffDate.valueOf() ||
      dropOffDate.valueOf() > maximumDropOffDate.valueOf()
    const preferredDropOffDate = shouldResetDropOffDate ? minimumAutomaticDropOffDate : dropOffDate
    const availableDropOffDate =
      findNearestOpenDate(
        dropOffOpeningTimes,
        preferredDropOffDate,
        shouldResetDropOffDate ? minimumAutomaticDropOffDate : availablePickUpDate,
        maximumDropOffDate
      ) ?? preferredDropOffDate

    setValue('pickUpDate', availablePickUpDate)
    if (shouldResetDropOffDate || availableDropOffDate.valueOf() !== dropOffDate.valueOf()) {
      setValue('dropOffDate', availableDropOffDate)
    }
  }

  const updateDropOffDate = (date: Date) => {
    if (isSameDay(date, pickUpDate)) {
      const pickUpTimePlusOneHour = addHourTo24hrString(pickUpTime)
      setValue('dropOffTime', pickUpTimePlusOneHour)
    }
    if (differenceInDays(date, pickUpDate) === MAX_DAYS_RENTAL) {
      setValue('dropOffTime', pickUpTime)
    }
    setValue('dropOffDate', date)
  }

  useEffect(() => {
    if (differenceInDays(dropOffDate, pickUpDate) === MAX_DAYS_RENTAL) {
      setValue('dropOffTime', pickUpTime)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickUpLocation])

  // A location change can make dates restored from defaults, a URL, or the previous location
  // invalid. Keep valid dates untouched; otherwise move forward to the first open day within the
  // existing bounds (or backwards only when the upper bound leaves no forward option).
  useEffect(() => {
    if (!pickUpLocation?.id) {
      return
    }

    const availablePickUpDate = findNearestOpenDate(
      pickUpOpeningTimes,
      pickUpDate,
      minPickUpDate,
      maxPickUpDate
    )
    const effectivePickUpDate = availablePickUpDate ?? pickUpDate
    if (availablePickUpDate && !isSameDay(availablePickUpDate, pickUpDate)) {
      setValue('pickUpDate', availablePickUpDate)
    }

    const maximumDropOffDate = addDays(effectivePickUpDate, MAX_DAYS_RENTAL)
    // When selecting a location moves pick-up off a closed day, move drop-off by the same number
    // of calendar days. Keeping the old drop-off can silently collapse the rental to one instant
    // (for example Sunday/Monday becoming Monday/Monday with unchanged times).
    const pickUpShiftInDays = differenceInCalendarDays(effectivePickUpDate, pickUpDate)
    const dropOffDateWithPreservedRentalLength = addDays(dropOffDate, pickUpShiftInDays)
    const preferredDropOffDate =
      dropOffDateWithPreservedRentalLength.valueOf() < effectivePickUpDate.valueOf()
        ? addDays(effectivePickUpDate, DEFAULT_RENTAL_LENGTH_DAYS)
        : dropOffDateWithPreservedRentalLength
    const availableDropOffDate = findNearestOpenDate(
      dropOffOpeningTimes,
      preferredDropOffDate,
      effectivePickUpDate,
      maximumDropOffDate
    )
    if (availableDropOffDate && !isSameDay(availableDropOffDate, dropOffDate)) {
      setValue('dropOffDate', availableDropOffDate)
    }
    // React only needs to rerun this adjustment when a location/schedule relationship changes;
    // updatePickUpDate owns customer-initiated date changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [differentDropOff, dropOffLocation, multiBrandEnabled, pickUpLocation])

  // A new pick-up can make the selected drop-off unservable (no brand in common). Revalidate
  // immediately - the customer keeps their drop-off when it is still compatible, and sees the
  // error at the moment they caused it rather than at submit.
  useEffect(() => {
    if (pickUpLocation?.id && differentDropOff && dropOffLocation?.id) {
      form.trigger('dropOffSearchString')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickUpLocation?.id])

  useEffect(() => {
    const pickUpLocationKey = `${defaultValues.pickUpLocation.id}:${defaultValues.pickUpLocation.type}`

    if (
      defaultValues.pickUpLocation.id !== '' &&
      appliedPickUpLocationRef.current !== pickUpLocationKey
    ) {
      setValue('pickUpLocation', defaultValues.pickUpLocation)
      setValue('pickUpSearchString', defaultValues.pickUpSearchString)
      appliedPickUpLocationRef.current = pickUpLocationKey
    }

    const dropOffLocationKey = defaultValues.dropOffLocation
      ? `${defaultValues.dropOffLocation.id}:${defaultValues.dropOffLocation.type}`
      : ''

    if (
      defaultValues.dropOffLocation?.id &&
      appliedDropOffLocationRef.current !== dropOffLocationKey
    ) {
      setValue('dropOffLocation', defaultValues.dropOffLocation)
      setValue('dropOffSearchString', defaultValues.dropOffSearchString)
      appliedDropOffLocationRef.current = dropOffLocationKey
    }
  }, [
    defaultValues.dropOffLocation,
    defaultValues.dropOffSearchString,
    defaultValues.pickUpLocation,
    defaultValues.pickUpSearchString,
    setValue,
  ])

  const submitForm: SubmitHandler<FieldValues> = (values) => {
    const {
      pickUpLocation,
      dropOffLocation,
      pickUpDate,
      pickUpTime,
      dropOffDate,
      dropOffTime,
      dob,
      membershipCode,
      pickUpSearchString,
      dropOffSearchString,
    } = values

    const { hours: pickUpHour, minutes: pickUpMinute } = getHoursMinutesFrom24hrString(pickUpTime)
    const { hours: dropOffHour, minutes: dropOffMinute } =
      getHoursMinutesFrom24hrString(dropOffTime)

    const pickUpDateWithTime = setHoursandMinutesOnDate(pickUpDate, pickUpHour, pickUpMinute)
    const dropOffDateWithTime = setHoursandMinutesOnDate(dropOffDate, dropOffHour, dropOffMinute)

    const locationChanged = hasLocationChanged({ id: pickUpLocation.id, type: pickUpLocation.type })

    const qualifiedBrandIds = getQualifiedBrandIdsForRequest(
      multiBrandEnabled,
      pickUpLocation,
      differentDropOff ? dropOffLocation : undefined,
      pickUpDateWithTime,
      dropOffDateWithTime
    )

    const searchData = {
      searchType: SEARCH_TYPE,
      dates: {
        startDateTime: format(pickUpDateWithTime, DATETIME_FORMAT),
        endDateTime: format(dropOffDateWithTime, DATETIME_FORMAT),
      },
      pickUpLocation: getLocationSearchData(
        pickUpLocation,
        differentDropOff ? [pickUpDateWithTime] : [pickUpDateWithTime, dropOffDateWithTime],
        multiBrandEnabled
      ),
      pickUpSearchString,
      ...(differentDropOff && {
        dropOffLocation: getLocationSearchData(
          dropOffLocation,
          [dropOffDateWithTime],
          multiBrandEnabled
        ),
        dropOffSearchString,
      }),
      travelers: [
        {
          ageGroup: 'ADULT',
          ...(isUnderAgeLimit && { dateOfBirth: format(dob, 'y-MM-dd') }),
        },
      ],
      sorting: DEFAULT_RECOMMENDED_SORTING,
      page: 1,
      // The legacy single field, kept working for consumers that never learned about the list.
      // It must be a brand that can actually serve the trip, not the pick-up row's own
      // vendorBrandId: a consolidated row always reports "3" there, so a Budget-only journey would
      // otherwise be searched as Avis.
      // Every brand that can serve this trip: shared by both ends, open when the car is collected,
      // and either open when it comes back or offering an after-hours key drop. Sending only these
      // means we never ask upstream for a shut desk - which it answers with a raw 500 - and a
      // one-way trip fans out to two calls only when two brands can genuinely serve it.
      ...getBrandSearchData(multiBrandEnabled, qualifiedBrandIds, pickUpLocation),
      membershipCode,
      opco: opCo,
      // Only inherit filters if location didn't change (dates/times changed only)
      ...(locationChanged ? {} : filtersQuery ?? {}),
    }

    if (toggleRef && typeof toggleRef !== 'function') {
      toggleRef?.current?.press()
    }

    router.push({
      pathname: createRoutePath('/cars/results'),
      query: stringify(searchData),
    })
  }

  return {
    form,
    submitForm,
    isUnderAgeLimit,
    setDateState,
    pickUpLocation,
    dropOffLocation,
    errors,
    updatePickUpDate,
    updateDropOffDate,
    pickUpDate,
    differentDropOff,
    maxDropOffDate,
    dropOffDate,
    isAvisPreferred,
  }
}
